<template>
  <div
    ref="svgContainer"
    class="svg-container"
    @mousedown="zoomPanHandler.startPanning"
    @mouseleave="zoomPanHandler.stopPanning"
    @mousemove="zoomPanHandler.pan"
    @mouseup="zoomPanHandler.stopPanning"
    @touchend="zoomPanHandler.handleTouchEnd"
    @touchmove="zoomPanHandler.handleTouchMove"
    @touchstart="zoomPanHandler.handleTouchStart"
    @wheel="zoomPanHandler.handleScroll"
  >
    <svg
      ref="svg"
      :style="{ transform: `scale(${zoomPanHandler.zoomLevel}) translate(${zoomPanHandler.offsetX}px, ${zoomPanHandler.offsetY}px)` }"
      viewBox="0 0 9328 7997"
      xmlns="http://www.w3.org/2000/svg"
    >
      <!-- Main SVG content -->
      <swimming-pool-map/>

      <!-- All umbrellas -->
      <foreignObject
        v-for="(position) in umbrellaButtonPositions"
        :id="`station-${position.id}`"
        :key="position.id"
        :class="`foreign-object-${position.type} station-${position.id}`"
        :height="isSafari() ? 300 : null"
        :width="isSafari() ? 300 : null"
        :x="position.x"
        :y="position.y"
        @mouseover="showTooltip($event, position)"
        @mouseleave="hideTooltip"
      >
        <div xmlns="http://www.w3.org/1999/xhtml">
          <umbrella-button
            :can-untoggle="allowUntoggle"
            :disabled="busy?.hasOwnProperty(position.id)"
            :isBooked="isMapInDashboard && busy?.[position.id]?.type === 'paid'"
            :selected="selectedStations.some(station => station.id === position.id)"
            :this-id="position.id"
            :type="position.type"
            @select-station="onSelect(position.id, position.type)"
          />
          <span v-if="showNumbers" class="station-number">{{ position.id }}</span>
        </div>
      </foreignObject>
    </svg>
    <div v-if="isMapInDashboard && tooltipVisible" :style="tooltipStyles" class="tooltip">
      <span v-if="tooltipBooking?.name">
        ID: <b>#{{ tooltipBooking?.id }}</b>
        <br>
        Nome: <b>{{ tooltipBooking?.name }}</b>
      </span>
      <span v-else>
        Pagamento in progress
      </span>
    </div>
  </div>

  <SunbedModal
    v-if="isModalVisible"
    :show-modal="isModalVisible"
    @close="closeModal"
    @confirm="handleConfirm"
  />
</template>

<script>
import SunbedModal from "@/Pages/Home/Checkout/partials/SunbedModal.vue";
import ZoomPanHandler from "@/Pages/Home/Checkout/partials/ZoomPanHandler.js";
import { umbrellaButtonPositions } from "@/Pages/Home/Checkout/partials/buttonPositions";
import SwimmingPoolMap from "@/Pages/Home/Checkout/partials/svg/Pool.vue";
import { isSafari } from "@/Shared/helper";
import UmbrellaButton from "./partials/UmbrellaButton.vue";

export default {
  components: {
    SunbedModal,
    SwimmingPoolMap,
    UmbrellaButton,
  },
  props: {
    busy: Object,
    showNumbers: Boolean,
    allowUntoggle: {
      type: Boolean,
      default: false,
    },
    askConfirm: {
      type: Boolean,
      default: true,
    },
    isMapInDashboard: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["updateStations"],
  data() {
    return {
      zoomPanHandler: new ZoomPanHandler(),
      selectedStations: [],
      umbrellaButtonPositions,
      isModalVisible: false,
      selectedExtraSunbeds: 1,
      selectedUmbrella: null,
      selectedType: null,
      tooltipVisible: false,
      tooltipBooking: null,
      tooltipStyles: {
        position: "absolute",
        top: "0px",
        left: "0px",
        transform: "translate(0, -100%)",
      },
    };
  },
  methods: {
    isSafari,
    clearSelectedStations() {
      this.selectedUmbrella = null;
      this.selectedStations = [];
    },
    onSelect(id, type) {
      this.selectedUmbrella = id;
      this.selectedType = type;

      const index = this.selectedStations.findIndex(
        (station) => station.id === this.selectedUmbrella,
      );
      if (index === -1) {
        if (type === "station") {
          if (this.askConfirm) {
            this.openModal(); // ask how many sun beds needed
          } else {
            this.handleConfirm(2);
          }
        } else {
          this.addUmbrella(); // add to the total directly
        }
      } else {
        // remove from total, previously selected one
        this.selectedStations.splice(index, 1);
        this.$emit("updateStations", this.selectedStations);
      }
    },
    openModal() {
      this.isModalVisible = true;
    },
    closeModal() {
      this.isModalVisible = false;
    },
    addUmbrella() {
      this.selectedStations.push({
        id: this.selectedUmbrella,
        type: this.selectedType,
        sunbedCount: 2, // default 2 for umbrella
        extraSunbedCount: 0, // default extra sunbeds
        extraAdultCount: 0,
      });
      this.$emit("updateStations", this.selectedStations);
    },
    handleConfirm(extraSunbeds, adults) {
      this.selectedExtraSunbeds = extraSunbeds;
      this.selectedExtraAdults = adults;

      this.selectedStations.push({
        id: this.selectedUmbrella,
        type: this.selectedType,
        sunbedCount: 2,
        extraSunbedCount: Number.parseInt(this.selectedExtraSunbeds),
        extraAdultCount: Number.parseInt(this.selectedExtraAdults),
      });

      this.$emit("updateStations", this.selectedStations);
      this.closeModal();
    },
    toggleFullScreen() {
      const element = this.$refs.svgContainer;
      if (!document.fullscreenElement) {
        if (element.requestFullscreen) {
          element.requestFullscreen();
        } else if (element.mozRequestFullScreen) {
          element.mozRequestFullScreen();
        } else if (element.webkitRequestFullscreen) {
          element.webkitRequestFullscreen();
        } else if (element.msRequestFullscreen) {
          element.msRequestFullscreen();
        }
      } else {
        if (document.exitFullscreen) {
          document.exitFullscreen();
        } else if (document.mozCancelFullScreen) {
          document.mozCancelFullScreen();
        } else if (document.webkitExitFullscreen) {
          document.webkitExitFullscreen();
        } else if (document.msExitFullscreen) {
          document.msExitFullscreen();
        }
      }
    },
    showTooltip(event, position) {
      if (!this.isMapInDashboard) {
        return;
      }
      const svgRect = this.$refs.svgContainer.getBoundingClientRect();
      const busyStation = this.busy?.[position.id];
      this.tooltipBooking = busyStation?.booking;
      this.tooltipStyles.top = `${event.clientY - svgRect.top}px`;
      this.tooltipStyles.left = `${event.clientX - svgRect.left}px`;
      this.tooltipVisible = Object.keys(this.tooltipBooking || {}).length > 0;
    },
    hideTooltip() {
      this.tooltipVisible = false;
    },
  },
};
</script>

<style>
.svg-container {
  width: 100%;
  height: 100%;
  overflow: hidden;
  position: relative;
  cursor: grab;
  touch-action: none;
}

.svg-container:active {
  cursor: grabbing;
}

.svg-container svg {
  width: 100%;
  height: 100%;
  transform-origin: 0 0;
}

.foreign-object-umbrella,
.foreign-object-station {
  width: 3.3%;
  height: 3.3%;
  overflow: hidden;
}

.station-number {
  width: 100%;
  height: 100%;
  font-size: 8rem;
  position: absolute;
  top: 2rem;
  left: -1rem;
  font-weight: bold;
  color: white;
  pointer-events: none;
  text-align: center;
}

.tooltip {
  text-align: center;
  background: white;
  padding: 5px;
  white-space: nowrap;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  pointer-events: none;
  z-index: 1000;
}

::backdrop {
  background-color: rgb(222 247 236 / var(--tw-bg-opacity));
}
</style>
